import { Injectable } from '@angular/core';
import { Observable, ObservableInput, of } from "rxjs";
import { IGetPropertyResponse, IProperty } from "libraries/service/resources/hotel/ISearchResponse";
import { ApiResourceService } from "libraries/service/apiResource/apiResource.service";
import { catchError, shareReplay } from "rxjs/operators";
import { IHeader } from "libraries/service/resource/IHeader";
import { HotelApiHeaderParameters } from "libraries/service/resources/hotel/hotel-api-header-parameters";
import { flatMap } from "rxjs/internal/operators";
import { HotelPropertyMappingService } from "libraries/service/resources/hotel/hotel-property-mapping.service";
import {LanguageService} from "../../language/language.service";

const DEFAULT_LANGUAGE: string = 'FR';
/**
 * Gets properties from the API. Keeps results in cache.
 */
@Injectable({
    providedIn: 'root'
})
export class PropertyResourceService {


    /** API endpoint 1st URL segment. */
    private static readonly HOTEL: string = 'hotel';
    /** API endpoint 2nd URL segment. */
    private static readonly PROPERTIES: string = 'properties';

    /** Common headers for hotel API Callas. */
    private readonly headerParameters: IHeader = HotelApiHeaderParameters.headerParameters;

    /** Cached properties. */
    private propertyCache: Map<string, IGetPropertyResponse> = new Map<string, IGetPropertyResponse>();
    private language: string = DEFAULT_LANGUAGE;

    constructor(
        private readonly apiResource: ApiResourceService,
        private readonly hotelPropertyMappingService: HotelPropertyMappingService,
        private readonly languageService: LanguageService
    ) {
        this.initLanguage();
    }

    private initLanguage(): void {
        const language: string = this.languageService.getUserLanguage();
        this.language = language && typeof language === 'string' ? language.toUpperCase() : DEFAULT_LANGUAGE;
    }

    /**
     * Gets a property form its unified syscode.
     * @param propertySyscode property unified syscode
     */
    public getProperty(propertySyscode: string): Observable<IGetPropertyResponse> {
        return this.getCachedProperty(this.apiResource.getApiUrl(
            PropertyResourceService.HOTEL,
            PropertyResourceService.PROPERTIES,
            propertySyscode));
    }

    /**
     * Gests a property form its hostCode and native property syscode
     * @param hostCode host code
     * @param propertySyscode property native syscode
     */
    public getHostProperty(hostCode: string, propertySyscode: string): Observable<IGetPropertyResponse> {
        this.initLanguage();
        return this.getCachedProperty(this.apiResource.getApiUrl(
            PropertyResourceService.HOTEL,
            PropertyResourceService.PROPERTIES,
            hostCode,
            propertySyscode));
    }

    public setPropertyInCache(property: IProperty): void {
        const url: string = this.apiResource.getApiUrl(
            PropertyResourceService.HOTEL,
            PropertyResourceService.PROPERTIES,
            property.hostCode,
            property.syscode);
        this.propertyCache.set(
            url,
            {property},
        );
    }

    private getCachedProperty(apiUrl: string): Observable<IGetPropertyResponse> {
        if (!this.propertyCache.has(apiUrl)) {
            const headerParameters: IHeader = {
                ...this.headerParameters,
                language: this.language
            };
            return this.apiResource.getOne<IGetPropertyResponse>(
                apiUrl,
                {},
                headerParameters
            ).pipe(
                flatMap((response: IGetPropertyResponse): Observable<IGetPropertyResponse> => {
                    const mappedResponse: IGetPropertyResponse = this.hotelPropertyMappingService.mapNewToOld(response);
                    this.propertyCache.set(apiUrl, mappedResponse);
                    return of(mappedResponse);
                }),
                // shareReplay(1),
                catchError((err: Error): ObservableInput<IGetPropertyResponse> => {
                    this.propertyCache.set(apiUrl, null);
                    throw new Error(err.message);
                })
            );
            // this.propertyCache.set(apiUrl, propertyObservable);
        }
        return of(this.propertyCache.get(apiUrl));
    }

}
